// src/Views/DominionProject.js — Flasher-styled detail page.
import React from 'react';
import { Helmet } from 'react-helmet';
import { Eyebrow, GhostButton } from '../components/primitives';
import Footer from './Footer';
import projectOverview from '../assets/images/Dominion_Project_Overview.jpg';

const linkStyle = {
  color: 'var(--magenta)',
  textDecoration: 'none',
  borderBottom: '1px dashed rgba(255,177,63,0.5)',
  paddingBottom: 1,
  transition: 'color 0.2s ease, border-color 0.2s ease',
};

const InlineLink = ({ href, children, ...rest }) => {
  const isExternal = /^https?:/i.test(href);
  return (
    <a
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      style={linkStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = 'var(--cyan)';
        e.currentTarget.style.borderColor = 'var(--cyan)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'var(--magenta)';
        e.currentTarget.style.borderColor = 'rgba(255,177,63,0.5)';
      }}
      {...rest}
    >
      {children}
    </a>
  );
};

const SectionHeading = ({ children }) => (
  <h2
    style={{
      fontFamily: 'var(--f-display)',
      fontSize: 'clamp(28px, 3.4vw, 40px)',
      fontWeight: 600,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      margin: '64px 0 20px',
      color: 'var(--ink)',
    }}
  >
    {children}
  </h2>
);

const Body = ({ children }) => (
  <p
    style={{
      fontSize: 17,
      lineHeight: 1.7,
      color: 'var(--ink-2)',
      margin: '0 0 18px',
    }}
  >
    {children}
  </p>
);

const Strong = ({ children }) => (
  <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{children}</strong>
);

const StatusPill = () => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      fontFamily: 'var(--f-mono)',
      fontSize: 11,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      color: 'var(--purple)',
      padding: '6px 14px',
      borderRadius: 999,
      background: 'rgba(160,102,255,0.12)',
      border: '1px solid var(--line-strong)',
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: 'var(--purple)',
        boxShadow: '0 0 8px rgba(160,102,255,0.6)',
      }}
    />
    In Progress · Narrative AI
  </span>
);

const DominionProject = () => {
  return (
    <>
      <Helmet>
        <title>Dominion Storyteller — Solomon Foshko | Design by Dialogue</title>
        <meta
          name="description"
          content="An AI-powered system that narrates and illustrates Dominion gameplay through generated stories and visuals, personalized to player decisions and tone."
        />
      </Helmet>

      <section
        style={{
          position: 'relative',
          padding: '160px 28px 80px',
          zIndex: 2,
        }}
      >
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <Eyebrow accent="var(--magenta)">⏵ BUILDS · DOMINION STORYTELLER</Eyebrow>

          <h1
            style={{
              fontFamily: 'var(--f-display)',
              fontSize: 'clamp(48px, 7vw, 96px)',
              fontWeight: 700,
              lineHeight: 1.0,
              letterSpacing: '-0.04em',
              margin: '0 0 24px',
              color: 'var(--ink)',
              paddingBottom: '0.1em',
            }}
          >
            Dominion{' '}
            <span
              style={{
                fontStyle: 'italic',
                fontWeight: 400,
                background: 'linear-gradient(120deg, var(--magenta), var(--purple), var(--cyan))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                paddingRight: '0.18em',
              }}
            >
              Storyteller.
            </span>
          </h1>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
            <StatusPill />
          </div>

          <div
            style={{
              padding: '20px 24px',
              borderRadius: 14,
              border: '1px dashed var(--line-strong)',
              background: 'rgba(160,102,255,0.04)',
              fontFamily: 'var(--f-mono)',
              fontSize: 13,
              lineHeight: 1.6,
              color: 'var(--ink-2)',
              marginBottom: 48,
            }}
          >
            <span style={{ color: 'var(--magenta)' }}>{'>'}</span>{' '}
            A living project log. This page is expected to keep growing — until I decide otherwise.
          </div>

          <SectionHeading>What is Dominion?</SectionHeading>
          <Body>
            For some context: <Strong>Dominion</Strong> is a physical deckbuilding game.{' '}
            <InlineLink href="https://wiki.dominionstrategy.com/index.php/Dominion">
              Here's a link to the game
            </InlineLink>{' '}
            and a{' '}
            <InlineLink href="https://www.youtube.com/watch?v=5jNGpgdMums">
              3-minute video
            </InlineLink>{' '}
            to help pull it all together.
          </Body>
          <Body>
            Here's the quick version: Dominion is a competitive card game where each player starts with a small,
            basic deck and gradually builds it by acquiring new cards from a shared marketplace. Over the course
            of the game, your deck becomes more powerful and efficient — letting you string together big plays,
            outmaneuver opponents, and rack up victory points.
          </Body>
          <Body>
            The twist? Point cards don't help your engine; they clog it. So you're constantly managing tempo,
            strategy, and opportunity, all while reacting to what other players are doing. It's elegant,
            strategic, and endlessly replayable.
          </Body>

          <SectionHeading>Why Dominion?</SectionHeading>
          <Body>There are a ton of reasons.</Body>
          <Body>
            Most importantly: <Strong>I know it inside and out.</Strong> I've played it regularly (often several
            times a week), and I own every expansion. It's been a comfort game, a strategic sandbox, and during
            Covid, a lifeline for staying connected with friends.
          </Body>
          <Body>
            Over time, our group started approaching the game with a little narrative flair. Instead of just
            optimizing plays, we began curating <Strong>thematic supply sets</Strong> and crafting{' '}
            <Strong>mini-stories</Strong> — especially during big attacks or point swings. It wasn't just
            gameplay, it was worldbuilding.
          </Body>
          <Body>That's where the seed of this project started.</Body>

          <SectionHeading>Why AI?</SectionHeading>
          <Body>
            As generative AI tools started to show up — text, image, video — I wanted to learn by building with
            something I knew intimately. <em style={{ color: 'var(--ink)', fontStyle: 'italic' }}>Dominion</em>{' '}
            became the ideal canvas: known rules, high variance, deep strategy, and a passionate personal connection.
          </Body>
          <Body>
            So this became my <Strong>learning-by-doing AI project</Strong> — a personal playground to explore
            storytelling systems, multi-modal output, and how player inputs can drive generative narrative.
          </Body>

          <SectionHeading>What am I building?</SectionHeading>
          <Body>
            <em style={{ color: 'var(--ink)', fontStyle: 'italic' }}>Let me reframe the question.</em>
          </Body>
          <Body>I'm not just building a tool.</Body>
          <Body>
            <Strong>I'm building an experience.</Strong>
          </Body>
          <Body>A system designed to answer the question:</Body>

          <blockquote
            style={{
              margin: '0 0 24px',
              padding: '20px 24px',
              borderLeft: '2px solid var(--magenta)',
              background: 'rgba(255,177,63,0.04)',
              borderRadius: '0 12px 12px 0',
              fontFamily: 'var(--f-display)',
              fontSize: 20,
              fontStyle: 'italic',
              lineHeight: 1.5,
              color: 'var(--ink)',
            }}
          >
            "What should it feel like to play Dominion and have the game tell your story?"
          </blockquote>

          <Body>That's the core of it. That's what everything else needs to ladder up to.</Body>
          <Body>
            So the next step is to break that into its component systems — how the narrative is formed, how
            visuals are created, how player decisions shape tone, and how each turn becomes a chapter in a larger story.
          </Body>

          <Body>
            Here's the working <Strong>North Star</Strong>:
          </Body>

          <p
            style={{
              fontFamily: 'var(--f-display)',
              fontSize: 'clamp(24px, 2.6vw, 32px)',
              fontWeight: 600,
              lineHeight: 1.25,
              letterSpacing: '-0.02em',
              margin: '0 0 24px',
              background: 'linear-gradient(120deg, var(--magenta), var(--cyan))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              paddingRight: '0.15em',
            }}
          >
            "Transform each game into a living saga."
          </p>

          <Body>It's an emotional north star. But let's anchor it with a more operational definition:</Body>

          <p
            style={{
              margin: '0 0 24px',
              padding: '18px 22px',
              border: '1px solid var(--line-strong)',
              borderRadius: 14,
              fontFamily: 'var(--f-body)',
              fontSize: 17,
              fontStyle: 'italic',
              lineHeight: 1.6,
              color: 'var(--ink)',
              background: 'rgba(15,13,42,0.55)',
            }}
          >
            "Build a system that narrates and illustrates Dominion gameplay through AI-generated stories and
            visuals, personalized to the player's decisions and tone."
          </p>

          <Body>That clarity helps me decide what needs to be built — and how the parts connect.</Body>
          <Body>That ends up looking like this:</Body>

          <div
            style={{
              margin: '24px 0 48px',
              display: 'flex',
              justifyContent: 'center',
              padding: 8,
              borderRadius: 18,
              border: '1px solid var(--line-strong)',
              background: 'rgba(15,13,42,0.55)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <img
              src={projectOverview}
              alt="Dominion Project Overview Diagram"
              style={{
                maxWidth: '100%',
                display: 'block',
                borderRadius: 12,
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
              }}
            />
          </div>

          <SectionHeading>Project Foundations</SectionHeading>
          <Body>This all ladders up to a modular system, which includes:</Body>

          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 32px',
              display: 'grid',
              gap: 12,
            }}
          >
            {[
              { name: 'Core Knowledge Layer', body: 'Dominion rules, card effects, expansion logic.', accent: 'var(--cyan)' },
              { name: 'Storytelling Layer', body: 'Templates for generating narrative based on turns.', accent: 'var(--magenta)' },
              { name: 'Personalization Layer', body: "Adapts to the group's tone, humor, and style.", accent: 'var(--purple)' },
              { name: 'Comic Engine', body: 'Visualizes key moments in comic-book format.', accent: 'var(--cyan)' },
              { name: 'Interface & Prompt Layer', body: 'Makes it easy to input game state and generate narrative output.', accent: 'var(--magenta)' },
              { name: 'Dynamic Feedback Loop', body: 'Learns from each session to evolve storytelling.', accent: 'var(--purple)' },
            ].map((item) => (
              <li
                key={item.name}
                style={{
                  position: 'relative',
                  padding: '16px 20px',
                  borderRadius: 14,
                  background: 'rgba(15,13,42,0.55)',
                  border: '1px solid var(--line)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0, left: 0, bottom: 0,
                    width: 3,
                    background: item.accent,
                    boxShadow: `0 0 14px ${item.accent}`,
                  }}
                />
                <div
                  style={{
                    fontFamily: 'var(--f-display)',
                    fontSize: 17,
                    fontWeight: 600,
                    color: 'var(--ink)',
                    marginBottom: 4,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {item.name}
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)' }}>
                  {item.body}
                </div>
              </li>
            ))}
          </ul>

          <div
            style={{
              marginTop: 48,
              paddingTop: 28,
              borderTop: '1px solid var(--line)',
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <GhostButton to="/" size="md">
              ← Back to home
            </GhostButton>
            <GhostButton to="/builds" size="md">
              See all builds
            </GhostButton>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default DominionProject;
